import { useCallback, useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Youtube from '@tiptap/extension-youtube';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Undo,
  Redo,
  Link2,
  Link2Off,
  Image as ImageIcon,
  Video,
  Check,
  X,
  Upload,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { supabase } from '@/lib/authBootstrap';
import { useToast } from '@/hooks/use-toast';
import API_BASE from '@/lib/apiBase';

interface BriefingRichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
}

type ActiveInput = 'link' | 'video' | null;

async function uploadBriefingImage(file: File): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const formData = new FormData();
  formData.append('image', file);

  const res = await fetch(`${API_BASE}/api/uploads/briefing-image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${session.access_token}` },
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: `Upload failed (${res.status})` }));
    throw new Error(err.error || `Upload failed (${res.status})`);
  }

  const { url } = await res.json();
  if (!url) throw new Error('Server returned no URL for uploaded image');
  return url;
}

export function BriefingRichTextEditor({
  content,
  onChange,
  placeholder,
  className,
}: BriefingRichTextEditorProps) {
  const { toast } = useToast();
  const [activeInput, setActiveInput] = useState<ActiveInput>(null);
  const [inputValue, setInputValue] = useState('');
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<ReturnType<typeof useEditor> | null>(null) as { current: ReturnType<typeof useEditor> | null };

  const handleImageUpload = useCallback(async (file: File) => {
    const editorInstance = editorRef.current;
    if (!editorInstance) {
      console.error('[BriefingRichTextEditor] No editor instance available');
      toast({ title: 'Editor not ready', description: 'Please try again in a moment.', variant: 'destructive' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Invalid file', description: 'Please select an image file.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    try {
      const url = await uploadBriefingImage(file);
      editorInstance.chain().focus().setImage({ src: url, alt: file.name }).run();
    } catch (err) {
      console.error('Image upload failed:', err);
      const msg = err instanceof Error ? err.message : 'Upload failed. Please try again.';
      toast({ title: 'Image upload failed', description: msg, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  }, [toast]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: { keepMarks: true, keepAttributes: false },
        orderedList: { keepMarks: true, keepAttributes: false },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800 cursor-pointer',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Image.configure({
        HTMLAttributes: { class: 'max-w-full rounded-lg my-2 border' },
      }),
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: { class: 'rounded-lg overflow-hidden my-3 w-full aspect-video' },
      }),
    ],
    content: content || '',
    onUpdate: ({ editor: ed }) => { onChange(ed.getHTML()); },
    editorProps: {
      attributes: {
        class: 'focus:outline-none p-3 text-sm leading-relaxed min-h-[120px]',
        'data-placeholder': placeholder || 'Start typing…',
      },
      handleDrop: (_view, event, _slice, moved) => {
        if (moved || !event.dataTransfer?.files?.length) return false;
        const file = event.dataTransfer.files[0];
        if (file?.type.startsWith('image/')) {
          event.preventDefault();
          handleImageUpload(file);
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) handleImageUpload(file);
            return true;
          }
        }
        return false;
      },
    },
    onCreate: ({ editor: ed }) => {
      if (!content || content.trim() === '' || content === '<p></p>' || content === '<p><br></p>') {
        ed.commands.setContent('');
      }
    },
  });

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) editor.commands.setContent(content || '');
    }
  }, [editor, content]);

  useEffect(() => {
    if (activeInput) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [activeInput]);

  if (!editor) return null;

  const openInput = (type: ActiveInput) => {
    if (type === 'link') {
      setInputValue(editor.getAttributes('link').href ?? '');
    } else {
      setInputValue('');
    }
    setActiveInput(type);
  };

  const closeInput = () => {
    setActiveInput(null);
    setInputValue('');
  };

  const commitInput = () => {
    const val = inputValue.trim();
    if (!val) { closeInput(); return; }

    if (activeInput === 'link') {
      const href = val.startsWith('http') ? val : `https://${val}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href, target: '_blank' }).run();
    } else if (activeInput === 'video') {
      editor.commands.setYoutubeVideo({ src: val, width: 640, height: 360 });
    }
    closeInput();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commitInput(); }
    if (e.key === 'Escape') closeInput();
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageUpload(file);
    e.target.value = '';
  };

  const inputMeta: Record<NonNullable<ActiveInput>, { placeholder: string; label: string }> = {
    link:  { placeholder: 'https://example.com', label: 'Link URL' },
    video: { placeholder: 'https://www.youtube.com/watch?v=...', label: 'YouTube URL' },
  };

  return (
    <div className={cn('border rounded-md bg-white', className)}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileSelect}
      />

      {/* Toolbar */}
      <div className="border-b px-2 py-1.5 flex flex-wrap items-center gap-0.5">
        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={cn('h-7 w-7 p-0', editor.isActive('bold') && 'bg-muted')} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={cn('h-7 w-7 p-0', editor.isActive('italic') && 'bg-muted')} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </Button>

        <div className="w-px h-5 bg-border mx-1" />

        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          className={cn('h-7 px-2 text-xs font-semibold', editor.isActive('heading', { level: 2 }) && 'bg-muted')}
          title="Heading">H2</Button>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          className={cn('h-7 px-2 text-xs font-semibold', editor.isActive('heading', { level: 3 }) && 'bg-muted')}
          title="Subheading">H3</Button>

        <div className="w-px h-5 bg-border mx-1" />

        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={cn('h-7 w-7 p-0', editor.isActive('bulletList') && 'bg-muted')} title="Bullet list">
          <List className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={cn('h-7 w-7 p-0', editor.isActive('orderedList') && 'bg-muted')} title="Numbered list">
          <ListOrdered className="h-3.5 w-3.5" />
        </Button>

        <div className="w-px h-5 bg-border mx-1" />

        {/* Media buttons */}
        <Button type="button" variant="ghost" size="sm"
          onClick={() => openInput(activeInput === 'link' ? null : 'link')}
          className={cn('h-7 w-7 p-0', (activeInput === 'link' || editor.isActive('link')) && 'bg-muted')}
          title="Insert link">
          <Link2 className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
          className={cn('h-7 w-7 p-0', uploading && 'opacity-50')}
          title="Upload image">
          {uploading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <ImageIcon className="h-3.5 w-3.5" />
          }
        </Button>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => openInput(activeInput === 'video' ? null : 'video')}
          className={cn('h-7 w-7 p-0', activeInput === 'video' && 'bg-muted')}
          title="Embed YouTube video">
          <Video className="h-3.5 w-3.5" />
        </Button>

        {editor.isActive('link') && !activeInput && (
          <Button type="button" variant="ghost" size="sm"
            onClick={() => editor.chain().focus().unsetLink().run()}
            className="h-7 w-7 p-0 text-red-400 hover:text-red-600" title="Remove link">
            <Link2Off className="h-3.5 w-3.5" />
          </Button>
        )}

        <div className="w-px h-5 bg-border mx-1" />

        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()} className="h-7 w-7 p-0" title="Undo">
          <Undo className="h-3.5 w-3.5" />
        </Button>
        <Button type="button" variant="ghost" size="sm"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()} className="h-7 w-7 p-0" title="Redo">
          <Redo className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Upload indicator */}
      {uploading && (
        <div className="border-b px-2 py-1.5 flex items-center gap-2 bg-blue-50 text-blue-700 text-xs">
          <Loader2 className="h-3 w-3 animate-spin" />
          Uploading image…
        </div>
      )}

      {/* Inline input bar — shown when link or video button is active */}
      {activeInput && (
        <div className="border-b px-2 py-1.5 flex items-center gap-2 bg-gray-50">
          <span className="text-xs text-muted-foreground shrink-0 w-20">
            {inputMeta[activeInput].label}
          </span>
          <Input
            ref={inputRef}
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={inputMeta[activeInput].placeholder}
            className="h-7 text-xs flex-1"
          />
          <Button type="button" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={commitInput}
            disabled={!inputValue.trim()} title="Insert">
            <Check className="h-3.5 w-3.5" />
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0"
            onClick={closeInput} title="Cancel">
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Editor area — capped height with internal scroll so long content
          (especially with embedded images) doesn't make the whole page huge. */}
      <div className="min-h-[120px] max-h-[360px] overflow-y-auto">
        <EditorContent editor={editor} className="rich-text-editor-content" />
      </div>
    </div>
  );
}
